// Creative tab: what the competition actually ran, not just what it spent.
//
// Every asset here is a Statex `ad_view` URL served from the provider's CDN, embedded rather
// than linked so the tab is a contact sheet you can read at a glance. Three sections, in the
// order the reference deck uses: the static mosaic, the video table, the video mosaic.
//
// `ad_view` serves HTML, not an image — see Tile. Each tile also keeps its figures and its
// "Open original" link outside the frame, so a creative the CDN declines to serve still
// reports its spend and impressions rather than vanishing from the mosaic and taking them
// with it. A cross-origin iframe cannot report a load failure to us, so there is no
// placeholder state pretending to detect one.

import { useState, useEffect } from 'react';
import { cmApi } from '../../api.js';
import { useClient } from '../../ClientContext.jsx';
import { fmtCurrency, fmtNumber } from '../../format.js';
import { Band, Panel, inkAlpha } from './Band.jsx';
import { T } from '../../../../shared/styles/tokens.js';

const MOSAIC = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
  gap: T.s4,
};

/** Impressions and spend, sat above the asset the way the reference deck stacks them. */
function Figures({ impressions, spend }) {
  const cell = {
    flex: 1, minWidth: 0, padding: '4px 8px', borderRadius: T.rSm,
    background: T.surface2, border: `1px solid ${T.ruleSoft}`,
  };
  return (
    <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
      <div style={cell}>
        <div className="o-eyebrow" style={{ fontSize: 9 }}>Impressions</div>
        <div className="o-mono" style={{ fontSize: 11, color: T.text }}>{fmtNumber(impressions)}</div>
      </div>
      <div style={cell}>
        <div className="o-eyebrow" style={{ fontSize: 9 }}>Spend (USD)</div>
        <div className="o-mono" style={{ fontSize: 11, color: T.text }}>{fmtCurrency(spend)}</div>
      </div>
    </div>
  );
}

/**
 * One mosaic tile: the creative embedded as it ran.
 *
 * `ad_view` is NOT an image URL. It serves text/html — a rendered replica of the placement,
 * Instagram chrome and all, with the real asset at `{ad_view}-assets/{hash}.{jpg,mp4}`. An
 * <img> or <video> pointed at it renders nothing, so the embed is an iframe. Verified against
 * the CDN: no X-Frame-Options, no CSP frame-ancestors, ACAO `*`.
 *
 * That also means the tile is identical for static and video — the page renders whichever it
 * holds. The static/video split only decides which mosaic a creative appears in.
 *
 * The sandbox is the point of care: this is third-party HTML with scripts. `allow-scripts`
 * and `allow-same-origin` are needed for the placement to render, and grant nothing over
 * this app because the frame is cross-origin. Everything that could reach out of the frame —
 * top-level navigation, forms, popups, downloads — stays off.
 */
function Tile({ creative, index }) {
  const frame = {
    position: 'relative', width: '100%', height: 300,
    borderRadius: T.rSm, overflow: 'hidden', background: T.surface2,
    border: `1px solid ${T.rule}`,
  };

  return (
    <div>
      <Figures impressions={creative.impressions} spend={creative.spend} />
      <div style={frame}>
        <iframe
          src={creative.ad_view}
          title={creative.title || `Creative ${index + 1}`}
          loading="lazy"
          referrerPolicy="no-referrer"
          sandbox="allow-scripts allow-same-origin"
          style={{ width: '100%', height: '100%', border: 0, display: 'block' }}
        />
      </div>
      <div style={{ marginTop: 6 }}>
        <div style={{ fontSize: T.tSmall, fontWeight: 600, color: T.text }}>
          {index + 1}. {creative.brand || '—'}
        </div>
        <div style={{ fontSize: 11, color: T.text3, overflow: 'hidden', textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap' }} title={creative.title || ''}>
          {creative.title || 'Untitled'}{creative.platform ? ` · ${creative.platform}` : ''}
        </div>
      </div>
      <a href={creative.ad_view} target="_blank" rel="noreferrer noopener"
         style={{ fontSize: 11, color: T.brass }}>Open original</a>
    </div>
  );
}

function VideoTable({ rows }) {
  if (!rows?.length) return <p className="o-state">No video creatives in this selection.</p>;
  const max = Math.max(...rows.map(r => Number(r.spend) || 0), 1);
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="o-tbl">
        <thead>
          <tr>
            <th>Ad copy</th><th>Brand</th><th>Creative title</th><th>Platform</th>
            <th style={{ textAlign: 'right' }}>Impressions</th>
            <th style={{ textAlign: 'right' }}>Spend (USD)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.ad_view}>
              <td>
                <a href={r.ad_view} target="_blank" rel="noreferrer noopener"
                   style={{ color: T.brass }}>Link</a>
              </td>
              <td className="o-strong">{r.brand || '—'}</td>
              <td>{r.title || 'Untitled'}</td>
              <td>{r.platform || '—'}</td>
              <td className="o-mono" style={{ textAlign: 'right' }}>{fmtNumber(r.impressions)}</td>
              {/* Heat on spend only: it is the column the eye is meant to rank by. */}
              <td className="o-mono" style={{
                textAlign: 'right',
                background: inkAlpha(0.06 + 0.5 * ((Number(r.spend) || 0) / max)),
              }}>{fmtCurrency(r.spend)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function CreativeOverview({ filters, calibrated }) {
  const { clientId } = useClient();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!clientId) return;
    setLoading(true); setErr(null);
    const params = Object.fromEntries(
      Object.entries(filters || {}).filter(([, v]) => v));
    cmApi.getCreatives(clientId, { ...params, calibrated: calibrated ? 'true' : 'false' })
      .then(setData)
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, [clientId, filters, calibrated]);

  if (!clientId) return <p className="o-state">Select a client to view creatives.</p>;
  if (loading) return <p className="o-state">Loading…</p>;
  if (err) return <p className="o-state o-error">{err}</p>;

  const statics = data?.static || [];
  const videos = data?.video || [];

  if (!statics.length && !videos.length) {
    return (
      <p className="o-hint">
        <b>No creatives in this selection.</b> Statex carries a creative URL on every row it
        has one for; nothing in the current filters has one.
      </p>
    );
  }

  return (
    <>
      <p className="o-note" style={{ marginBottom: T.s5 }}>
        {data.total} creatives, ranked by spend. Assets are served from the Statex CDN and
        embedded directly — figures are {data.calibration_basis === 'mixed' ? 'a mix of calibrated and raw Statex' : data.calibrated ? 'calibrated' : 'raw Statex (unadjusted)'}.
      </p>

      <Band title="Ad Mosaic (Static)" sub={`${statics.length} shown, highest spend first`}>
        <Panel>
          {statics.length === 0
            ? <p className="o-state">No static creatives in this selection.</p>
            : <div style={MOSAIC}>
                {statics.map((c, i) => (
                  <Tile key={c.ad_view} creative={c} index={i} />
                ))}
              </div>}
        </Panel>
      </Band>

      <Band title="Top Performing Video Ads" sub="Video creatives ranked by spend">
        <Panel><VideoTable rows={videos} /></Panel>
      </Band>

      <Band title="Ad Mosaic (Video)" sub={`${videos.length} shown, highest spend first`}>
        <Panel>
          {videos.length === 0
            ? <p className="o-state">No video creatives in this selection.</p>
            : <div style={MOSAIC}>
                {videos.map((c, i) => (
                  <Tile key={c.ad_view} creative={c} index={i} />
                ))}
              </div>}
        </Panel>
      </Band>
    </>
  );
}
